import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { PieChart, PieValueType } from '@mui/x-charts-pro'
import map from 'lodash/map'

import { useListLocationsQuery } from '../../../../services/options'
import {
  formatDateTime,
  useEventsTypeOptions,
  useParticipantStatusOptions,
  getOption,
} from '../../../../utils'
import { Events, ParticipantStatus } from '../../../../types'
import EventStatusTag from '../../../events/components/EventStatusTag'
import { Dot } from './Dot'

interface Props {
  event: Events<true> | null | undefined
}

export default function AdminEventDetails({ event }: Props) {
  const { t } = useTranslation('admin')
  const { data: locationsData } = useListLocationsQuery()
  const eventsTypeOptions = useEventsTypeOptions(t)
  const participantStatusOptions = useParticipantStatusOptions(t)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const location = useMemo(
    () => locationsData?.find(elem => elem.id === event?.location),
    [event?.location, locationsData]
  )

  const type = useMemo(
    () => getOption(eventsTypeOptions, undefined, event?.type),
    [eventsTypeOptions, event?.type]
  )

  const data: PieValueType[] = useMemo(() => {
    const arr = map(
      event?.stats?.participant_status ?? [],
      (value: number, key: ParticipantStatus) => {
        const status = participantStatusOptions.find(el => el.value === key)
        return status
          ? ({
              id: key as string,
              value,
              label: `${value}: ` + (status?.label ?? ''),
              color: status?.color ?? '',
            } as PieValueType)
          : undefined
      }
    )
    return arr.filter(Boolean) as unknown as PieValueType[]
  }, [event?.stats?.participant_status, participantStatusOptions])

  return (
    <Stack direction="column">
      <Stack direction={{ md: 'column', lg: 'row' }}>
        <Stack direction="column" flex="3 0">
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Type')} :</h6>
              <span>
                {type ? (
                  <Chip
                    color="primary"
                    size="small"
                    sx={{
                      backgroundColor: type?.color,
                    }}
                    label={type?.label}
                  />
                ) : (
                  'NC'
                )}
              </span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Statut')} :</h6>
              <EventStatusTag size="small" status={event?.status} />
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Début')} :</h6>
              <span>{event?.start ? formatDateTime(event.start) : 'NC'}</span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Fin')} :</h6>
              <span>{event?.end ? formatDateTime(event.end) : 'NC'}</span>
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Localisation')} :</h6>
              <span>{location ? `${location.name} ${location.city}` : 'NC'}</span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre présent')} :</h6>
              <Dot backgroundColor={theme => theme.palette.primary.main}>
                {event?.stats?.total_present ?? 'NC'}
              </Dot>
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre attente partenaire')} :</h6>
              <Dot
                backgroundColor={
                  participantStatusOptions.find(el => el.value === ParticipantStatus.PendingPartner)
                    ?.color
                }
              >
                {event?.stats?.participant_status.pending_partner ?? 'NC'}
              </Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre de confirmés')} :</h6>
              <Dot
                backgroundColor={
                  participantStatusOptions.find(el => el.value === ParticipantStatus.Confirmed)
                    ?.color
                }
              >
                {event?.stats?.participant_status.confirmed ?? 'NC'}
              </Dot>
            </Stack>
          </Stack>
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre paiement en attente')} :</h6>
              <Dot
                backgroundColor={
                  participantStatusOptions.find(
                    el => el.value === ParticipantStatus.PendingCheckout
                  )?.color
                }
              >
                {event?.stats?.participant_status.pending_checkout ?? 'NC'}
              </Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre annulés')} :</h6>
              <Dot
                backgroundColor={
                  participantStatusOptions.find(el => el.value === ParticipantStatus.Cancelled)
                    ?.color
                }
              >
                {event?.stats?.participant_status.cancelled ?? 'NC'}
              </Dot>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" flex={{ md: '3 0', lg: '2 0' }}>
          <PieChart
            series={[
              {
                data,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: isMobile ? 'left' : 'right',
                },
                itemMarkWidth: 2,
                itemMarkHeight: 20,
                markGap: 5,
                itemGap: 5,
              },
            }}
            margin={
              isMobile
                ? { top: 0, right: 0, bottom: 0, left: 200 }
                : { top: 0, right: 200, bottom: 0, left: 0 }
            }
            height={150}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
